import { useEffect, useRef } from 'react';

/**
 * PauseOnScroll
 * Pauses any <video> and stops any <iframe> (by resetting its src) that are
 * inside `children` when the wrapper leaves the viewport. Re-entering does not
 * autoplay unless the iframe/video attributes force it.
 *
 * Usage:
 *   <PauseOnScroll>
 *     <video ... />
 *   </PauseOnScroll>
 */
export default function PauseOnScroll({ children, threshold = 0.15, className = '', onLeave }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

    const stopMedia = () => {
      el.querySelectorAll('video').forEach((v) => {
        try { v.pause(); } catch (_) { /* noop */ }
      });
      el.querySelectorAll('iframe').forEach((i) => {
        const src = i.getAttribute('src');
        if (src) {
          // Reloading the iframe stops playback across providers (YouTube,
          // Ultimedia, Vimeo, …) without relying on provider-specific APIs.
          i.setAttribute('src', src);
        }
      });
      if (typeof onLeave === 'function') {
        try { onLeave(); } catch (_) { /* noop */ }
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) stopMedia();
        });
      },
      { threshold: [threshold], rootMargin: '0px 0px 0px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, onLeave]);

  return (
    <div ref={ref} className={className} data-pause-on-scroll="">
      {children}
    </div>
  );
}
